import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { UIRootState } from '../store/reducers';
import { GameStats } from './GameStats';
import { GameButtons } from './GameButtons.tsx';
import { SettingsModal } from './modals/SettingsModal.tsx';
import { DesignModal } from './modals/DesignModal';
import { rootElement } from 'ui/index.ts';

export const GameApp: React.FC = () => {
  const dispatch = useDispatch();
  const { difficulty, moves, time, showDifficulty } = useSelector((state: UIRootState) => state.gameUI.gameStats);
  const settingsOpen = useSelector((state: UIRootState) => state.gameUI.modals.settingsModal.isOpen);
  const designOpen = useSelector((state: UIRootState) => state.gameUI.modals.designModal.isOpen);
  const tutorialActive = useSelector((state: UIRootState) => state.gameUI.tutorialActive);
  const isInitialized = useSelector((state: UIRootState) => state.gameUI.isInitialized);
  const labels = useSelector((state: UIRootState) => state.gameUI.labels);
  const leftX = useSelector((state: UIRootState) => state.gameUI.gameStats.leftX);
  const rightX = useSelector((state: UIRootState) => state.gameUI.gameStats.rightX);
  const buttons = useSelector((state: UIRootState) => state.gameUI.buttons);
  
  const gameUIRef = useRef<HTMLDivElement>(null);
  const tabletButtonsRef = useRef<HTMLDivElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Управление анимацией кнопок
  useEffect(() => {
    const buttons = tabletButtonsRef.current?.querySelectorAll('button:not(#moreSettingsButton)') as NodeListOf<HTMLButtonElement>;
    if (!buttons) return;

    if (mobileMenuOpen) {
      // Открытие: показываем кнопки и анимируем появление
      buttons.forEach(button => {
        button.style.display = 'flex';
        button.style.opacity = '0';
        button.classList.add('animating');
        
        // Запуск анимации через requestAnimationFrame для правильного transition
        requestAnimationFrame(() => {
          button.style.opacity = '1';
        });
      });
    } else {
      // Закрытие: анимируем исчезновение, потом скрываем
      buttons.forEach(button => {
        button.classList.add('animating');
        button.style.opacity = '0';
        
        // После анимации скрываем кнопки
        setTimeout(() => {
          button.style.display = 'none';
          button.classList.remove('animating');
        }, 300);
      });
    }
  }, [mobileMenuOpen]);


  useEffect(() => {
    if (designOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    const canvas = document.querySelector('#solitaire-container canvas');
    
    if (canvas && rootElement) {
      rootElement.style.width = canvas.clientWidth + 'px';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [designOpen]);

  const handleButtonClick = (buttonKey: string, data?: any) => {
    document.dispatchEvent(new CustomEvent('ui-button-click', {
      detail: { buttonKey, data }
    }));
  };

  // Animation logic for panels
  useEffect(() => {
    console.log('🔥 Opening animation useEffect:', { settingsOpen, designOpen });
    
    let panelAnimationInProgress = false;

    const animatePanel = (panelId: string, isOpen: boolean) => {
      console.log('🎯 animatePanel called:', { panelId, isOpen });
      
      if (!isOpen) {
        console.log('🔄 Panel is closing, skipping opening animation');
        return;
      }
      
      const panel = document.getElementById(panelId);
      const root = document.getElementById('game-ui');
      const stats = document.getElementById('game-ui-stats');
      const gameButtons = document.getElementById('game-buttons');
      const tabletButtons = document.querySelector('.tablet-buttons');

      if (panel && !panelAnimationInProgress) {
        panelAnimationInProgress = true;

        // Скрыть tablet buttons
        if (tabletButtons) {
          (tabletButtons as HTMLElement).style.transition = 'opacity 0.36s ease-in-out';
          (tabletButtons as HTMLElement).style.opacity = '0';
          (tabletButtons as HTMLElement).style.pointerEvents = 'none';
        }

        // Анимация панели
        panel.style.zIndex = '1';
        panel.style.display = 'block';
        panel.style.transition = '';
        panel.style.transform = 'translateX(100%)';
        panel.style.opacity = '0';
        panel.style.transition = 'transform 0.36s ease-in-out, opacity 0.36s ease-in-out';
        
        requestAnimationFrame(() => {
          panel.style.transform = 'translateX(0)';
          panel.style.opacity = '1';
        });

        const isLowHeight = window.innerHeight <= 682;
        const isLandscape = window.matchMedia('(orientation: landscape)').matches;
        const isTablet = window.matchMedia('(max-width: 1038px)').matches || (isLowHeight && isLandscape);

        // Расширить root панель - разная логика для планшета и десктопа
        if (root) {

          if (isTablet) {
            // Планшетный режим - добавляем класс
            root.classList.add('tablet-open');
            root.style.background = 'rgb(31, 31, 31, 0.8)';
          } else {
            // Десктопный режим - меняем ширину
            root.style.transition = 'width 0.36s ease-in-out, background 0.36s ease-in-out, border-radius 0.36s ease-in-out';
            root.style.width = '392px';
            root.style.background = 'rgb(31, 31, 31, 0.8)';
          }
        }

        // Скрыть статистику
        if (stats) {
          stats.style.transition = 'opacity 0.36s ease-in-out';
          stats.style.opacity = '0';
          setTimeout(() => {
            stats.style.display = 'none';
          }, 360);
        }

        // Изменить ширину кнопок - только для десктопа
        if (gameButtons) {
          if (!isTablet) {
            // Десктопный режим
            gameButtons.style.width = 'calc(100% - 392px)';
          } else {
            // Планшетный режим - затемнить кнопки
            gameButtons.style.opacity = '0.2';
          }
        }

        setTimeout(() => {
          panelAnimationInProgress = false;
        }, 360);
      }
    };

    animatePanel('game-ui-options', settingsOpen);
    animatePanel('game-ui-design', designOpen);
  }, [settingsOpen, designOpen]);

  // Анимации закрытия панелей (как в tmp)
  useEffect(() => {
    if (!settingsOpen && !designOpen) {
      const root = document.getElementById('game-ui');
      const options = document.getElementById('game-ui-options');
      const design = document.getElementById('game-ui-design');
      const stats = document.getElementById('game-ui-stats');
      const gameButtons = document.getElementById('game-buttons');
      const tabletButtons = document.querySelector('.tablet-buttons');

      const isLowHeight = window.innerHeight <= 682;
      const isLandscape = window.matchMedia('(orientation: landscape)').matches;

      const isTablet = window.matchMedia('(max-width: 1038px)').matches || (isLowHeight && isLandscape);

      // Сворачиваем root панель - разная логика для планшета и десктопа
      if (root) {
        
        if (isTablet) {
          // Планшетный режим - убираем класс
          root.classList.remove('tablet-open');
          root.style.background = '';
        } else {
          // Десктопный режим - меняем ширину
          root.style.transition = 'width 0.36s ease-in-out, background 0.36s ease-in-out';
          root.style.borderRadius = '0';
          root.style.width = '120px';
          root.style.background = '';
        }
      }

      // Восстанавливаем ширину кнопок
      if (gameButtons) {
        const isTablet = window.matchMedia('(max-width: 1038px)').matches;
        
        if (!isTablet) {
          // Только для десктопа восстанавливаем ширину
          gameButtons.style.width = 'calc(100% - 120px)';
        }
        gameButtons.style.opacity = '1';
      }

      // Скрываем панели
      if (options) {
        options.style.transition = 'transform 0.36s ease-in-out, opacity 0.36s ease-in-out';
        options.style.transform = 'translateX(100%)';
        options.style.opacity = '0';
        setTimeout(() => {
          options.style.setProperty('display', 'none', 'important');
          options.style.zIndex = '-1';
          options.style.transform = '';
          options.style.opacity = '';
        }, 360);
      }

      if (design) {
        design.style.transition = 'transform 0.36s ease-in-out, opacity 0.36s ease-in-out';
        design.style.transform = 'translateX(100%)';
        design.style.opacity = '0';
        setTimeout(() => {
          design.style.setProperty('display', 'none', 'important');
          design.style.zIndex = '-1';
          design.style.transform = '';
          design.style.opacity = '';
        }, 360);
      }

      // Показываем статистику
      if (stats) {
        stats.style.display = 'flex';
        stats.style.opacity = '0';
        stats.style.transition = 'opacity 0.36s ease-in-out';
        
        requestAnimationFrame(() => {
          stats.style.opacity = '1';
        });
      }

      // Показываем tablet buttons
      if (tabletButtons) {
        (tabletButtons as HTMLElement).style.transition = 'opacity 0.36s ease-in-out';
        (tabletButtons as HTMLElement).style.opacity = '1';
        (tabletButtons as HTMLElement).style.pointerEvents = 'auto';
        
        // В планшетном режиме восстанавливаем opacity отдельных кнопок, 
        // которые могли быть скрыты из-за mobileMenuOpen = false
        if (isTablet) {
          const buttons = tabletButtons.querySelectorAll('button:not(#moreSettingsButton)') as NodeListOf<HTMLButtonElement>;
          buttons.forEach(button => {
            if (button.style.display !== 'none') {
              button.style.opacity = '1';
            }
          });
        }
      }

      // if (window.matchMedia('(min-width: 1200px)').matches) {
      //   setTimeout(() => {
      //     if (root) {
      //       root.style.borderRadius = '0px 15px 15px 0px';
      //     }
      //   }, 360);
      // }
    }
  }, [settingsOpen, designOpen]);

  // Глобальные обработчики событий
  useEffect(() => {
    // Обработчик кликов вне панели
    const handleClickOutside = (e: MouseEvent) => {
      if (gameUIRef.current && !gameUIRef.current.contains(e.target as Node) && !tabletButtonsRef.current?.contains(e.target as Node)) {
        if (settingsOpen || designOpen) {
          console.log('🔄 Click outside detected, closing panels...');
          if (settingsOpen) {
            handleButtonClick('close-settings');
          }
          if (designOpen) {
            handleButtonClick('close-design');
          }
        }
      }
      
      // Закрываем мобильное меню при клике вне его
      const tabletButtons = document.querySelector('.tablet-buttons');
      if (tabletButtons && !tabletButtons.contains(e.target as Node) && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    // Обработчик ESC клавиши
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        console.log('🔄 ESC pressed, closing panels...');
        if (settingsOpen) {
          handleButtonClick('close-settings');
        }
        if (designOpen) {
          handleButtonClick('close-design');
        }
        setMobileMenuOpen(false);
      }
    };

    // Обработчик ресайза
    const handleResize = () => {
      console.log('🔄 Window resize, closing panels...');
      if (settingsOpen) {
        handleButtonClick('close-settings');
      }
      if (designOpen) {
        handleButtonClick('close-design');
      }
      
      // В планшетном режиме не сбрасываем mobileMenuOpen при ресайзе
      const isTablet = window.matchMedia('(max-width: 1038px)').matches;
      if (!isTablet) {
        setMobileMenuOpen(false);
      }
    };

    // Обработчик смены вкладки
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('🔄 Tab hidden, closing panels...');
        if (settingsOpen) {
          handleButtonClick('close-settings');
        }
        if (designOpen) {
          handleButtonClick('close-design');
        }
        
        // В планшетном режиме не сбрасываем mobileMenuOpen при смене вкладки
        const isTablet = window.matchMedia('(max-width: 1038px)').matches;
        if (!isTablet) {
          setMobileMenuOpen(false);
        }
      }
    };

    // Добавляем обработчики событий
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Очистка при размонтировании
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, settingsOpen, designOpen, mobileMenuOpen]);

  // Скрываем UI до инициализации
  if (!isInitialized) {
    return null;
  }

  // Скрываем UI полностью во время туториала
  if (tutorialActive) {
    return null;
  }

  return (
    <>
      {/* Mobile Game Stats */}
      <div 
        id="game-ui-stats-mobile" 
        className={buttons.magicWandDisabled ? 'magic-wand-active' : ''}
        style={{
          zIndex: 1,
          justifySelf: 'start',
          marginLeft: '5px',
          pointerEvents: 'inherit'
        }}
      >
        {showDifficulty && (
          <h5 id="difficulty-text" style={{
            fontSize: '19px',
            textTransform: 'capitalize',
            fontWeight: 400,
            right: `${rightX}px`
          }} className="text-light mb-0">
            {difficulty}
          </h5>
        )}
        
        <div className="text-center d-flex flex-row" style={{
          top: '5px',
          position: 'absolute',
          left: `${leftX}px`,
          pointerEvents: 'inherit'
        }}>
          <div className="d-flex flex-row" style={{

            gap: '8px',
            alignItems: 'center'
          }}>
            <div id="moves-label" style={{ fontSize: '14px' }} className="text-light opacity-75">
              {labels.movesLabel}
            </div>
            <div className="text-light mb-0" id="moves-count" style={{ fontWeight: 400, fontSize: '18px' }}>
              {moves}
            </div>
          </div>
          
          <div className="d-flex flex-row " style={{

           alignItems: 'center',
            gap: '8px',
            marginLeft: '20px'
          }}>
            <span className="stat-label text-light opacity-75" style={{ fontSize: '14px', alignItems: 'center' }}>{labels.timeLabel}</span>
            <span className="stat-value text-light" style={{ fontWeight: 400, fontSize: '18px' }}>{typeof time === 'number' ? time : time}</span>
          </div>
        </div>
      </div>

      {/* Main Game UI Panel */}
      <div 
        ref={gameUIRef}
        id="game-ui" 
        className={`h-100`}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '120px',
          zIndex: 2,
          transition: 'width 0.36s ease-in-out, background 0.36s ease-in-out, border-radius 0.36s ease-in-out'
        }}
      >
        <GameStats />
        <SettingsModal />
        <DesignModal />
      </div>

      {/* Tablet Buttons - Adaptive positioning */}
      <div ref={tabletButtonsRef} className="tablet-buttons align-items-center justify-content-center">
        <button 
          id="shareButton"
          className="circle-btn btn btn-outline-light border-0 rounded-circle position-relative d-flex align-items-center justify-content-center p-0"
          style={{ width: '55px', height: '55px' }}
          onClick={() => handleButtonClick('share')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <g filter="url(#share-filter)">
              <path d="M19.019 7.09096C19.019 7.45759 19.0632 7.81407 19.1464 8.1554L10.9146 12.8484C10.1058 12.0651 9.00422 11.5825 7.79177 11.5825C5.3151 11.5825 3.30078 13.5966 3.30078 16.0735C3.30078 18.5504 5.3151 20.5645 7.79177 20.5645C9.00434 20.5645 10.106 20.0818 10.9148 19.2984L19.1464 23.9911C19.0632 24.3325 19.019 24.689 19.019 25.0557C19.019 27.5324 21.0331 29.5467 23.51 29.5467C25.9869 29.5467 28.001 27.5324 28.001 25.0557C28.001 22.5788 25.9869 20.5645 23.51 20.5645C22.2064 20.5645 21.0309 21.1225 20.2099 22.0123L12.0886 17.3824C12.2149 16.9681 12.283 16.5286 12.283 16.0735C12.283 15.6182 12.2149 15.1786 12.0884 14.7642L20.2098 10.1343C21.0308 11.0241 22.2063 11.5822 23.51 11.5822C25.9869 11.5822 28.001 9.56784 28.001 7.09096C28.001 4.61429 25.9869 2.59998 23.51 2.59998C21.0331 2.59998 19.019 4.61429 19.019 7.09096Z" fill="white"/>
            </g>
            <defs>
              <filter id="share-filter" x="3.30078" y="2.59998" width="24.7002" height="28.0104" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="1.06367"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_share"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_share" result="shape"/>
              </filter>
            </defs>
          </svg>
        </button>
        
        <button 
          id="tutorialButton"
          className="circle-btn btn btn-outline-light border-0 rounded-circle position-relative d-flex align-items-center justify-content-center p-0"
          style={{ width: '55px', height: '55px' }}
          onClick={() => handleButtonClick('tutorial')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <g filter="url(#tutorial-filter)">
              <path d="M14.2 26.3197C14.7062 26.4986 15.25 26.1268 15.25 25.5902V7.82711C15.25 7.62943 15.175 7.43175 15.0156 7.30937C14.0969 6.57513 11.9875 5.63379 9.25 5.63379C6.88281 5.63379 4.67031 6.25978 3.34844 6.7681C2.81875 6.9752 2.5 7.50235 2.5 8.07186V25.5008C2.5 26.0609 3.1 26.4515 3.62969 26.2774C5.10625 25.7832 7.44531 25.2137 9.25 25.2137C10.8391 25.2137 12.9531 25.8726 14.2 26.3197ZM17.8 26.3197C19.0469 25.8726 21.1609 25.2137 22.75 25.2137C24.5547 25.2137 26.8938 25.7832 28.3703 26.2774C28.9 26.4562 29.5 26.0609 29.5 25.5008V8.07186C29.5 7.50235 29.1812 6.9752 28.6516 6.77281C27.3297 6.25978 25.1172 5.63379 22.75 5.63379C20.0125 5.63379 17.9031 6.57513 16.9844 7.30937C16.8297 7.43175 16.75 7.62943 16.75 7.82711V25.5902C16.75 26.1268 17.2984 26.4986 17.8 26.3197Z" fill="white"/>
            </g>
            <defs>
              <filter id="tutorial-filter" x="2.5" y="5.63379" width="27" height="21.6313" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="0.899982"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_tutorial"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_tutorial" result="shape"/>
              </filter>
            </defs>
          </svg>
        </button>
        
        <button 
          id="designButton"
          className="circle-btn btn btn-outline-light border-0 rounded-circle position-relative d-flex align-items-center justify-content-center p-0"
          style={{ width: '55px', height: '55px' }}
          onClick={() => {
            handleButtonClick('design');
            setTimeout(() => {
              const designContent = document.querySelector('.design-content') as HTMLElement;
                if (designContent) {
                  designContent.scrollTop = 0;
                }
              }, 10);
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <g filter="url(#design-filter)">
              <path d="M15.9989 27.4275C14.437 27.4275 12.9608 27.1273 11.5703 26.5269C10.1798 25.9265 8.96536 25.1075 7.92688 24.0697C6.88917 23.032 6.07012 21.8179 5.46974 20.4275C4.86936 19.037 4.56955 17.5608 4.57031 15.9989C4.57031 14.4179 4.88003 12.9322 5.49946 11.5417C6.11889 10.1513 6.95698 8.94174 8.01374 7.91317C9.0705 6.8846 10.3036 6.0705 11.7132 5.47088C13.1227 4.87127 14.6275 4.57107 16.2275 4.57031C17.7513 4.57031 19.1894 4.83241 20.5417 5.3566C21.8941 5.88079 23.08 6.6046 24.0995 7.52803C25.1181 8.45146 25.9276 9.54669 26.528 10.8137C27.1284 12.0808 27.4282 13.4473 27.4275 14.9132C27.4275 17.1036 26.7608 18.7848 25.4275 19.9566C24.0941 21.1284 22.4751 21.7139 20.5703 21.7132H18.456C18.2846 21.7132 18.1654 21.7608 18.0983 21.856C18.0313 21.9513 17.9981 22.056 17.9989 22.1703C17.9989 22.3989 18.1417 22.7276 18.4275 23.1566C18.7132 23.5856 18.856 24.0758 18.856 24.6275C18.856 25.5798 18.5939 26.2846 18.0697 26.7417C17.5456 27.1989 16.8553 27.4275 15.9989 27.4275ZM9.71317 17.1417C10.2084 17.1417 10.6179 16.9798 10.9417 16.656C11.2656 16.3322 11.4275 15.9227 11.4275 15.4275C11.4275 14.9322 11.2656 14.5227 10.9417 14.1989C10.6179 13.8751 10.2084 13.7132 9.71317 13.7132C9.21793 13.7132 8.80841 13.8751 8.4846 14.1989C8.16079 14.5227 7.99889 14.9322 7.99889 15.4275C7.99889 15.9227 8.16079 16.3322 8.4846 16.656C8.80841 16.9798 9.21793 17.1417 9.71317 17.1417ZM13.1417 12.5703C13.637 12.5703 14.0465 12.4084 14.3703 12.0846C14.6941 11.7608 14.856 11.3513 14.856 10.856C14.856 10.3608 14.6941 9.95127 14.3703 9.62746C14.0465 9.30365 13.637 9.14174 13.1417 9.14174C12.6465 9.14174 12.237 9.30365 11.9132 9.62746C11.5894 9.95127 11.4275 10.3608 11.4275 10.856C11.4275 11.3513 11.5894 11.7608 11.9132 12.0846C12.237 12.4084 12.6465 12.5703 13.1417 12.5703ZM18.856 12.5703C19.3513 12.5703 19.7608 12.4084 20.0846 12.0846C20.4084 11.7608 20.5703 11.3513 20.5703 10.856C20.5703 10.3608 20.4084 9.95127 20.0846 9.62746C19.7608 9.30365 19.3513 9.14174 18.856 9.14174C18.3608 9.14174 17.9513 9.30365 17.6275 9.62746C17.3036 9.95127 17.1417 10.3608 17.1417 10.856C17.1417 11.3513 17.3036 11.7608 17.6275 12.0846C17.9513 12.4084 18.3608 12.5703 18.856 12.5703ZM22.2846 17.1417C22.7798 17.1417 23.1894 16.9798 23.5132 16.656C23.837 16.3322 23.9989 15.9227 23.9989 15.4275C23.9989 14.9322 23.837 14.5227 23.5132 14.1989C23.1894 13.8751 22.7798 13.7132 22.2846 13.7132C21.7894 13.7132 21.3798 13.8751 21.056 14.1989C20.7322 14.5227 20.5703 14.9322 20.5703 15.4275C20.5703 15.9227 20.7322 16.3322 21.056 16.656C21.3798 16.9798 21.7894 17.1417 22.2846 17.1417Z" fill="white"/>
            </g>
            <defs>
              <filter id="design-filter" x="4.57031" y="4.57031" width="22.8574" height="23.8571" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="1"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_design"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_design" result="shape"/>
              </filter>
            </defs>
          </svg>
        </button>
        
        <button 
          id="settingsButton"
          className="circle-btn btn btn-outline-light border-0 rounded-circle position-relative d-flex align-items-center justify-content-center p-0"
          style={{ width: '55px', height: '55px' }}
          onClick={() => handleButtonClick('settings')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <g filter="url(#settings-filter)">
              <path d="M24.5812 15.875C24.5812 15.6111 24.5697 15.3587 24.5469 15.0949L26.675 13.4772C27.1327 13.133 27.2585 12.4906 26.9725 11.9858L24.8329 8.2801C24.6957 8.03709 24.4738 7.85348 24.2098 7.76454C23.9459 7.6756 23.6584 7.68761 23.4027 7.79825L20.9428 8.84226C20.5194 8.54397 20.0732 8.2801 19.6041 8.06212L19.2723 5.41194C19.2036 4.8383 18.7117 4.40234 18.1396 4.40234H13.8719C13.2883 4.40234 12.7964 4.8383 12.7277 5.41194L12.3959 8.06212C11.9268 8.2801 11.4806 8.54397 11.0572 8.84226L8.59729 7.79825C8.07098 7.5688 7.45313 7.77531 7.16709 8.2801L5.02751 11.9972C4.74147 12.502 4.86733 13.133 5.325 13.4887L7.45313 15.1063C7.4057 15.6215 7.4057 16.14 7.45313 16.6551L5.325 18.2728C4.86733 18.617 4.74147 19.2594 5.02751 19.7642L7.16709 23.4699C7.45313 23.9747 8.07098 24.1812 8.59729 23.9518L11.0572 22.9077C11.4806 23.206 11.9268 23.4699 12.3959 23.6879L12.7277 26.3381C12.7964 26.9117 13.2883 27.3477 13.8604 27.3477H18.1281C18.7002 27.3477 19.1922 26.9117 19.2609 26.3381L19.5927 23.6879C20.0618 23.4699 20.508 23.206 20.9313 22.9077L23.3913 23.9518C23.9176 24.1812 24.5354 23.9747 24.8215 23.4699L26.961 19.7642C27.2471 19.2594 27.1212 18.6284 26.6636 18.2728L24.5354 16.6551C24.5697 16.3913 24.5812 16.1389 24.5812 15.875ZM16.0458 19.8904C13.8375 19.8904 12.0412 18.0892 12.0412 15.875C12.0412 13.6608 13.8375 11.8596 16.0458 11.8596C18.254 11.8596 20.0503 13.6608 20.0503 15.875C20.0503 18.0892 18.254 19.8904 16.0458 19.8904Z" fill="white"/>
            </g>
            <defs>
              <filter id="settings-filter" x="4.875" y="4.40234" width="22.25" height="23.9453" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="1"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_settings"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_settings" result="shape"/>
              </filter>
            </defs>
          </svg>
        </button>
        
        <button 
          id="moreSettingsButton"
          className="circle-btn btn btn-outline-light border-0 rounded-circle position-relative align-items-center justify-content-center p-0"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{ width: '55px', height: '55px' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
            <g filter="url(#more-menu-filter-1)">
              <circle cx="4.85826" cy="13.9286" r="3.42857" fill="white"/>
            </g>
            <g filter="url(#more-menu-filter-2)">
              <circle cx="14.0028" cy="13.9286" r="3.42857" fill="white"/>
            </g>
            <g filter="url(#more-menu-filter-3)">
              <circle cx="23.1434" cy="13.9286" r="3.42857" fill="white"/>
            </g>
            <defs>
              <filter id="more-menu-filter-1" x="1.42969" y="10.5" width="6.85742" height="7.68779" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="0.830608"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_more_menu_1"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_more_menu_1" result="shape"/>
              </filter>
              <filter id="more-menu-filter-2" x="10.5742" y="10.5" width="6.85742" height="7.68779" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="0.830608"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_more_menu_2"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_more_menu_2" result="shape"/>
              </filter>
              <filter id="more-menu-filter-3" x="19.7148" y="10.5" width="6.85742" height="7.68779" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="0.830608"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_more_menu_3"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_more_menu_3" result="shape"/>
              </filter>
            </defs>
          </svg>
        </button>
      </div>

      {/* Magic Wand Button */}
      <div 
        id="game-buttons" 
        className="d-flex justify-content-center" 
        style={{
          gap: '14px',
          height: '84px',
          position: 'absolute',
          bottom: 0,
          width: 'calc(100% - 120px)',
          transition: 'width 0.36s ease-in-out'
        }}
      >
        <GameButtons />
      </div>
    </>
  );
}; 